<template>
    <address-sub-element-layout
        v-bind="subElementLayoutModel"
        v-on:update="sendDataChange"
        v-on:same-as-selected="sameAsSelected"
        v-on:validated="onAddressValidated"
        v-on:update-has-location-validation-errors="updateHasLocationValidationErrors"
        v-on:help-clicked="$emit('help-clicked')">
        <template v-slot:help>
            <slot name="help" />
        </template>
        <template v-slot:beforeAddress>
            <!-- Name -->
            <div class="row gx-3 align-items-end">
                <pc-input-group v-bind:class="responsiveHelpers.standard">
                    <pc-input
                        v-bind:id="element.PublicId + 'First'"
                        v-bind:validation-group-id="nameGroup"
                        v-bind:model-value="first"
                        v-bind:is-sub-label="true"
                        v-bind:group-id="element.DataFieldGroupId"
                        v-bind:autocomplete="BROWSER_AUTOCOMPLETE_TOKEN.givenName"
                        v-bind:required="requiredSubfields.Name"
                        v-bind:validator="contactValidators.name"
                        v-bind:readonly="readonly"
                        v-bind:show-validation-errors="false"
                        v-bind:placeholder="firstNamePlaceholder"
                        v-bind:label="nameLabel"
                        v-bind:data-test-role="testRole.first"
                        v-on:update:model-value="sendDataChange(firstId, $event)"
                        v-on:validated="onContactValidated('name', $event)" />
                </pc-input-group>
                <pc-input-group v-bind:class="responsiveHelpers.standard">
                    <pc-input
                        v-bind:validation-group-id="nameGroup"
                        v-bind:model-value="last"
                        v-bind:group-id="element.DataFieldGroupId"
                        v-bind:autocomplete="BROWSER_AUTOCOMPLETE_TOKEN.familyName"
                        v-bind:readonly="readonly"
                        v-bind:validator="contactValidators.name"
                        v-bind:show-validation-errors="false"
                        v-bind:placeholder="lastNamePlaceholder"
                        v-bind:data-test-role="testRole.last"
                        v-on:update:model-value="sendDataChange(lastId, $event)"
                        v-on:validated="onContactValidated('name', $event)" />
                </pc-input-group>
                <validation-render-helper
                    v-bind:validation-group-id="nameGroup"
                    v-bind:validation-error="nameValidationError" />
            </div>
            <!-- Company -->
            <div class="row gx-3">
                <pc-input-group v-bind:class="responsiveHelpers.standard">
                    <pc-input
                        v-bind:validation-group-id="companyGroup"
                        v-bind:model-value="company"
                        v-bind:group-id="element.DataFieldGroupId"
                        v-bind:autocomplete="BROWSER_AUTOCOMPLETE_TOKEN.organization"
                        v-bind:readonly="readonly"
                        v-bind:is-sub-label="true"
                        v-bind:required="requiredSubfields.Company"
                        v-bind:validator="contactValidators.company"
                        v-bind:show-validation-errors="false"
                        v-bind:placeholder="companyPlaceholder"
                        v-bind:label="companyLabel"
                        v-bind:data-test-role="testRole.company"
                        v-on:update:model-value="sendDataChange(companyId, $event)"
                        v-on:validated="onContactValidated('company', $event)" />
                </pc-input-group>
                <validation-render-helper
                    v-bind:validation-group-id="companyGroup"
                    v-bind:validation-error="companyValidationError" />
            </div>
        </template>
        <template v-slot:afterAddress>
            <!-- Email -->
            <div class="row gx-3">
                <pc-input-group v-bind:class="responsiveHelpers.standard">
                    <pc-input
                        v-bind:validation-group-id="emailGroup"
                        v-bind:model-value="email"
                        v-bind:group-id="element.DataFieldGroupId"
                        v-bind:autocomplete="BROWSER_AUTOCOMPLETE_TOKEN.email"
                        v-bind:readonly="readonly"
                        v-bind:is-sub-label="true"
                        v-bind:required="requiredSubfields.Email"
                        v-bind:validator="contactValidators.email"
                        v-bind:show-validation-errors="false"
                        v-bind:placeholder="emailPlaceholder"
                        v-bind:label="emailLabel"
                        v-bind:data-test-role="testRole.email"
                        inputmode="email"
                        v-on:update:model-value="sendDataChange(emailId, $event)"
                        v-on:validated="onContactValidated('email', $event)" />
                </pc-input-group>
                <validation-render-helper
                    v-bind:validation-group-id="emailGroup"
                    v-bind:validation-error="emailValidationError" />
            </div>
            <!-- Phone -->
            <div class="row gx-3">
                <pc-input-group v-bind:class="responsiveHelpers.standard">
                    <template v-if="enforcePhoneFormat">
                        <pc-formatted-number
                            v-bind:validation-group-id="phoneGroup"
                            v-bind:model-value="phone"
                            v-bind:group-id="element.DataFieldGroupId"
                            v-bind:autocomplete="BROWSER_AUTOCOMPLETE_TOKEN.off"
                            v-bind:readonly="readonly"
                            v-bind:is-sub-label="true"
                            v-bind:required="requiredSubfields.Phone"
                            v-bind:validator="contactValidators.phone"
                            v-bind:show-validation-errors="false"
                            v-bind:label="phoneLabel"
                            v-bind:data-test-role="testRole.phone"
                            format="(###) ###-####"
                            inputmode="tel"
                            use-format-as-placeholder
                            v-on:update:model-value="sendDataChange(phoneId, $event)"
                            v-on:validated="onContactValidated('phone', $event)" />
                    </template>
                    <template v-else>
                        <pc-input
                            v-bind:validation-group-id="phoneGroup"
                            v-bind:model-value="phone"
                            v-bind:group-id="element.DataFieldGroupId"
                            v-bind:autocomplete="`${BROWSER_AUTOCOMPLETE_TOKEN.mobile} ${BROWSER_AUTOCOMPLETE_TOKEN.tel}`"
                            v-bind:readonly="readonly"
                            v-bind:is-sub-label="true"
                            v-bind:required="requiredSubfields.Phone"
                            v-bind:validator="contactValidators.phone"
                            v-bind:show-validation-errors="false"
                            v-bind:placeholder="phoneUnformattedPlaceholder"
                            v-bind:label="phoneLabel"
                            v-bind:data-test-role="testRole.phone"
                            inputmode="tel"
                            v-on:update:model-value="sendDataChange(phoneId, $event)"
                            v-on:validated="onContactValidated('phone', $event)" />
                    </template>
                </pc-input-group>
                <validation-render-helper
                    v-bind:validation-group-id="phoneGroup"
                    v-bind:validation-error="phoneValidationError" />
            </div>
        </template>
    </address-sub-element-layout>
</template>

<script>
    import addressSubElement from "./addressSubElement.js";
    import { SUFFIXES, mapGroupIdsAndValues, getValidator, VALIDATOR_TYPES } from "./_elementBase";
    import { ContactRequiredSubfields } from "./contactRequiredSubfields";
    import { BROWSER_AUTOCOMPLETE_TOKEN } from "Util/browserAutocompleteConstants";
    import { AUX_MUTATION } from "Forms/Components/ProjectForm/projectFormStore";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/contact.i18n";

    export default {
        extends: addressSubElement,
        emits: ["help-clicked"],
        data() {
            return {
                contactValidationResult: {
                    name: null,
                    company: null,
                    email: null,
                    phone: null
                },
                BROWSER_AUTOCOMPLETE_TOKEN,
                hasLocationValidationErrors: false
            };
        },
        computed: {
            requiredSubfields() {
                const required = this.element.Properties.required;
                return new ContactRequiredSubfields(required);
            },
            enforcePhoneFormat() {
                return this.getBooleanPropertyValue("enforcephoneformat");
            },
            contactValidators() {
                const reqValidator = getValidator(this.$app, [{ type: VALIDATOR_TYPES.required }]);

                const emailValidatorOptions = [];
                if (this.requiredSubfields.Email) {
                    emailValidatorOptions.push({ type: VALIDATOR_TYPES.required });
                }
                emailValidatorOptions.push({ type: VALIDATOR_TYPES.email });

                const phoneValidatorOptions = [];
                if (this.requiredSubfields.Phone) {
                    phoneValidatorOptions.push({ type: VALIDATOR_TYPES.required });
                }
                if (this.enforcePhoneFormat) {
                    phoneValidatorOptions.push({ type: VALIDATOR_TYPES.phone });
                }

                const validators = {
                    name: this.requiredSubfields.Name ? reqValidator : null,
                    company: this.requiredSubfields.Company ? reqValidator : null,
                    email: getValidator(this.$app, emailValidatorOptions),
                    phone: phoneValidatorOptions.length > 0 ? getValidator(this.$app, phoneValidatorOptions) : null
                };

                return validators;
            },
            ...mapGroupIdsAndValues({
                first: SUFFIXES.Contact.First,
                last: SUFFIXES.Contact.Last,
                company: SUFFIXES.Contact.Company,
                email: SUFFIXES.Contact.Email,
                phone: SUFFIXES.Contact.Phone
            }),
            nameValidationError() {
                return this.getValidationError(this.contactValidationResult.name);
            },
            companyValidationError() {
                return this.getValidationError(this.contactValidationResult.company);
            },
            emailValidationError() {
                return this.getValidationError(this.contactValidationResult.email);
            },
            phoneValidationError() {
                return this.getValidationError(this.contactValidationResult.phone);
            },
            nameGroup() {
                return this.element.PublicId + "Name";
            },
            companyGroup() {
                return this.element.PublicId + "Company";
            },
            emailGroup() {
                return this.element.PublicId + "Email";
            },
            phoneGroup() {
                return this.element.PublicId + "Phone";
            },
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            )
        },
        methods: {
            getValidationError(validationResult) {
                if (validationResult) {
                    return validationResult.error;
                }
                return null;
            },
            onContactValidated(prop, validationResult) {
                this.contactValidationResult[prop] = validationResult;
            },
            updateHasLocationValidationErrors(value) {
                this.hasLocationValidationErrors = value;
                this.$store.commit(AUX_MUTATION, {
                    elementId: this.element.PublicId,
                    auxState: { hasLocationValidationErrors: value }
                });
            }
        }
    };
</script>
